<template>
    <ModalBase
        :name="'editTransaccion-' + transaccion.id"
        title="Editar transacción"
        @close="close(false)"
    >
        <ValidationObserver ref="observer" tag="form" @submit.prevent="submit">
            <div class="tw:space-y-4">

                <BaseMultiselect
                    v-model="localTransaccion.metodo_pago"
                    name="método de pago"
                    label="Método de pago"
                    :options="metodos_pago"
                    value-key="id"
                    label-key="nombre"
                    :disabled="loading"
                />

                <BaseInputText
                    v-model="localTransaccion.fecha"
                    label="Fecha de la transacción"
                    name="fecha_transaccion"
                    type="datetime-local"
                    :disabled="loading"
                />

                <!-- Descuento -->
                <div class="tw:border-t tw:border-gray-200 tw:pt-4">
                    <p class="tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                        Descuento
                    </p>

                    <div class="tw:flex tw:gap-2 tw:mb-3">
                        <Button
                            :variant="discountMode === 'percentage' ? 'primary' : 'border'"
                            class="tw:flex-1"
                            :disabled="loading"
                            @click.prevent="setDiscountMode('percentage')"
                        >
                            Descuento %
                        </Button>
                        <Button
                            :variant="discountMode === 'fixed' ? 'primary' : 'border'"
                            class="tw:flex-1"
                            :disabled="loading"
                            @click.prevent="setDiscountMode('fixed')"
                        >
                            Descuento €
                        </Button>
                    </div>

                    <div v-if="discountMode === 'percentage'" class="tw:space-y-2">
                        <BaseMultiselect
                            v-model="discountPercentage"
                            :options="percentageOptions"
                            label="Porcentaje de descuento"
                            label-key="name"
                            value-key="id"
                            :disabled="loading"
                        />
                    </div>

                    <div v-if="discountMode === 'fixed'" class="tw:space-y-2">
                        <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700">
                            Importe del descuento
                        </label>
                        <div class="tw:flex tw:gap-2 tw:items-center">
                            <input
                                v-model.number="discountFixed"
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Ej: 100"
                                class="tw:flex-1 tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-lg tw:text-sm tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500"
                                :disabled="loading"
                            />
                            <span class="tw:text-gray-600 tw:font-medium">€</span>
                        </div>
                    </div>
                </div>

                <!-- Botón -->
                <div class="tw:flex tw:justify-end tw:pt-2">
                    <Button type="submit" :disabled="loading">Guardar</Button>
                </div>

            </div>
        </ValidationObserver>
    </ModalBase>
</template>

<script setup>
    import { ref, computed } from 'vue'
    import store from '@/store'
    import { config } from '@/config'
    import { useToast } from '@/composables/common/useToast'
    import { useEditarTransaccion } from '@/composables/admin/transacciones/useEditarTransaccion'
    import ModalBase from '@/modules/admin/ui/ModalBase'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        transaccion: {
            type: Object,
            required: true
        },
        params: {
            type: Object,
            default: () => ({})
        },
        vuexAlias: {
            type: String,
            default: 'back.abonados.transacciones.editar'
        }
    })

    const emit = defineEmits(['cerrar'])
    const toast = useToast()
    const observer = ref(null)

    const { localTransaccion, metodos_pago, loading, submitTransaction } = useEditarTransaccion(
        props.transaccion,
        {
            store,
            config,
            toast,
            onComplete: actualizar => close(actualizar),
            vuexAlias: props.vuexAlias
        }
    )

    const close = actualizar => {
        emit('cerrar', 'editTransaccion-' + props.transaccion.id, actualizar)
    }

    // --- Descuento ---

    // Los porcentajes de descuento vienen de la config del backend,
    // igual que en la compra de abonos en taquilla
    const percentageOptions = computed(() => {
        const raw = store.getters.getConfig?.descuento_aplicado || []
        const mapped = raw.map(opt => ({ id: opt.valor, name: opt.label }))
        return [{ id: 0, name: 'Sin descuento' }, ...mapped]
    })

    const discountMode = ref('percentage')
    const discountPercentage = ref(null)
    const discountFixed = ref(null)

    // Inicializar desde valores existentes de la transacción
    if (props.transaccion.descuento_integro && props.transaccion.descuento_integro > 0) {
        discountMode.value = 'fixed'
        discountFixed.value = props.transaccion.descuento_integro
    } else if (props.transaccion.descuento_aplicado && props.transaccion.descuento_aplicado > 0) {
        discountMode.value = 'percentage'
        discountPercentage.value = props.transaccion.descuento_aplicado
    } else {
        discountPercentage.value = 0
    }

    const setDiscountMode = mode => {
        discountMode.value = mode
        if (mode === 'percentage') {
            discountFixed.value = null
        } else {
            discountPercentage.value = null
        }
    }

    const submit = async() => {
        const isValid = await observer.value.validate()
        if (isValid) {
            // Sincronizar descuento en localTransaccion antes de enviar
            localTransaccion.descuento_aplicado = discountMode.value === 'percentage'
                ? (discountPercentage.value ?? 0)
                : 0
            localTransaccion.descuento_integro = discountMode.value === 'fixed'
                ? (discountFixed.value ?? 0)
                : 0
            submitTransaction(props.params)
        }
    }
</script>
